import { useEffect, useState } from "react";
import { APP_ROLES, type AppRole } from "@shop-ledger/shared-types";
import { Button } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { getRoleDefaultPermissions, getUserPermissions, listPermissions, updateUserPermissions } from "../../api/permissionsApi";
import { listUsers } from "../../api/usersApi";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";

/**
 * Permissions — OWNER-only. Two views:
 *  1. Role defaults (`GET /roles/{role}/permissions`) — read-only, per §11's design (there
 *     is deliberately no PATCH for editing a role's default set in this backend pass).
 *  2. Per-user overrides (`GET/PATCH /users/{id}/permissions`) — grant/revoke individual
 *     permissions beyond (or below) a user's role default, or reset to the role default.
 *
 * This is the ONLY UI surface that can grant a non-OWNER role the
 * `purchase_order.approve`/`approval_rule.manage` permissions the Approval Requests/Rules
 * pages otherwise gate to OWNER by platform default.
 */
export function PermissionsPage() {
  const { user, ensureFreshToken } = useAuth();
  const isOwner = user?.role === "OWNER";

  const permissionsState = useAsyncData(() => ensureFreshToken().then((token) => listPermissions(token)), []);
  const usersState = useAsyncData(() => ensureFreshToken().then((token) => listUsers(token)), []);

  const [selectedRole, setSelectedRole] = useState<AppRole>("OWNER");
  const roleDefaultsState = useAsyncData(
    () => ensureFreshToken().then((token) => getRoleDefaultPermissions(token, selectedRole)),
    [selectedRole],
  );

  const [selectedUserId, setSelectedUserId] = useState("");
  const userPermsState = useAsyncData(
    () => (selectedUserId ? ensureFreshToken().then((token) => getUserPermissions(token, selectedUserId)) : Promise.resolve(null)),
    [selectedUserId],
  );

  const [drafts, setDrafts] = useState<Record<string, boolean | null>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    setDrafts({});
    setSaveSuccess(false);
    setSaveError(null);
  }, [selectedUserId]);

  if (!isOwner) {
    return (
      <div className="page">
        <div className="page__header">
          <div>
            <h1 className="page__title">Permissions</h1>
          </div>
        </div>
        <StatusBanner kind="info">Permission management is OWNER-only. Your role ({user?.role}) doesn't have access to these routes.</StatusBanner>
      </div>
    );
  }

  const catalog = permissionsState.data ?? [];
  const effectiveCodes = new Set(userPermsState.data?.effective_permission_codes ?? []);
  const overrideByCode = new Map((userPermsState.data?.overrides ?? []).map((o) => [o.permission_code, o.granted]));

  function draftFor(code: string): boolean | null | undefined {
    return code in drafts ? drafts[code] : overrideByCode.get(code);
  }

  function cycleDraft(code: string) {
    const current = draftFor(code);
    // Cycle: no override -> grant -> revoke -> no override.
    const next = current === undefined ? true : current === true ? false : null;
    setDrafts((prev) => ({ ...prev, [code]: next }));
  }

  async function handleSave() {
    if (!selectedUserId || Object.keys(drafts).length === 0) return;
    setSaveError(null);
    setSaveSuccess(false);
    setSaving(true);
    try {
      const token = await ensureFreshToken();
      await updateUserPermissions(token, selectedUserId, {
        overrides: Object.entries(drafts).map(([permission_code, granted]) => ({ permission_code, granted })),
      });
      setDrafts({});
      setSaveSuccess(true);
      userPermsState.reload();
    } catch (error) {
      setSaveError(error instanceof ApiError ? error.message : "Could not save permission overrides.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Permissions</h1>
          <p className="page__subtitle">Role default permission sets (read-only) and per-user overrides.</p>
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">Role defaults (read-only)</h2>
        <div className="filter-bar">
          <div className="sl-field">
            <label className="sl-field__label" htmlFor="role-select">
              Role
            </label>
            <select id="role-select" className="sl-input" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value as AppRole)}>
              {APP_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>
        {roleDefaultsState.error ? <StatusBanner kind="error">{roleDefaultsState.error}</StatusBanner> : null}
        {roleDefaultsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            <p className="muted">
              {roleDefaultsState.data?.is_tenant_specific ? "This tenant has customized this role's defaults." : "Platform-wide default (this tenant hasn't customized this role)."}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(roleDefaultsState.data?.permission_codes ?? []).length === 0 ? (
                <span className="muted">No default permissions.</span>
              ) : (
                roleDefaultsState.data?.permission_codes.map((code) => (
                  <span key={code} className="badge badge--current">
                    {code}
                  </span>
                ))
              )}
            </div>
          </>
        )}
      </div>

      <div className="panel">
        <h2 className="panel__title">Per-user overrides</h2>
        <div className="filter-bar">
          <div className="sl-field">
            <label className="sl-field__label" htmlFor="user-select">
              User
            </label>
            <select id="user-select" className="sl-input" value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} disabled={usersState.loading}>
              <option value="">Select a user…</option>
              {(usersState.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email} ({u.role})
                </option>
              ))}
            </select>
          </div>
        </div>

        {!selectedUserId ? (
          <p className="muted">Select a user to view and edit their effective permissions.</p>
        ) : userPermsState.loading ? (
          <p className="muted">Loading…</p>
        ) : userPermsState.error ? (
          <StatusBanner kind="error">{userPermsState.error}</StatusBanner>
        ) : (
          <>
            <p className="muted">
              Role default: <strong>{userPermsState.data?.role}</strong>. Click a permission to cycle: role default → granted → revoked → back to role
              default. Effective set shown checked/unchecked below.
            </p>
            {permissionsState.error ? <StatusBanner kind="error">{permissionsState.error}</StatusBanner> : null}
            <table className="sl-data-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th className="sl-data-table__th">Permission</th>
                  <th className="sl-data-table__th">Category</th>
                  <th className="sl-data-table__th">Effective?</th>
                  <th className="sl-data-table__th">Override</th>
                </tr>
              </thead>
              <tbody>
                {catalog.map((permission) => {
                  const draft = draftFor(permission.code);
                  const isEffective = draft === undefined ? effectiveCodes.has(permission.code) : draft === true ? true : draft === false ? false : effectiveCodes.has(permission.code);
                  const overrideLabel = draft === undefined ? "role default" : draft === true ? "granted" : draft === false ? "revoked" : "role default";
                  return (
                    <tr key={permission.code} className="sl-data-table__row">
                      <td className="sl-data-table__td">
                        <code>{permission.code}</code>
                        <div className="muted">{permission.description}</div>
                      </td>
                      <td className="sl-data-table__td">{permission.category ?? "—"}</td>
                      <td className="sl-data-table__td">
                        <span className={`badge ${isEffective ? "badge--current" : "badge--overdue"}`}>{isEffective ? "Yes" : "No"}</span>
                      </td>
                      <td className="sl-data-table__td">
                        <button type="button" className="sl-button sl-button--ghost sl-button--md" onClick={() => cycleDraft(permission.code)}>
                          {overrideLabel}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {saveError ? <StatusBanner kind="error">{saveError}</StatusBanner> : null}
            {saveSuccess ? <StatusBanner kind="info">Saved.</StatusBanner> : null}
            <div className="form-actions" style={{ marginTop: 12 }}>
              <Button type="button" disabled={saving || Object.keys(drafts).length === 0} onClick={() => void handleSave()}>
                {saving ? "Saving…" : "Save overrides"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
